import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, MapPin, Bed, Maximize2, Compass, ArrowLeft, Home, ArrowUpDown } from "lucide-react";
import { useLocation } from "wouter";
import { formatDistanceToNow } from "date-fns";

export default function ApartmentMarketplace() {
  const [, navigate] = useLocation();
  const [filters, setFilters] = useState({
    bhk: undefined as number | undefined,
    listingType: undefined as "rent" | "sale" | undefined,
    minPrice: undefined as number | undefined,
    maxPrice: undefined as number | undefined,
    minArea: undefined as number | undefined,
    maxArea: undefined as number | undefined,
    floor: undefined as number | undefined,
    facing: undefined as string | undefined,
    apartmentType: undefined as string | undefined,
    orderBy: "newest" as "newest" | "priceAsc" | "priceDesc" | "mostViewed",
  });

  const { data: listings, isLoading } = trpc.apartments.listPublic.useQuery(filters);

  const clearFilters = () => {
    setFilters({
      bhk: undefined,
      listingType: undefined,
      minPrice: undefined,
      maxPrice: undefined,
      minArea: undefined,
      maxArea: undefined,
      floor: undefined,
      facing: undefined,
      apartmentType: undefined,
      orderBy: "newest",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation Bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.history.back()}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            >
              <Home className="w-4 h-4" />
              Home
            </Button>
          </div>
        </div>
      </div>

      <div className="py-12">
        <div className="container mx-auto px-4">
          {/* Hero */}
          <div className="mb-10">
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Apartment Marketplace</h1>
            <p className="text-lg text-slate-600">729 apartments. Only verified listings inside Swanlake.</p>
          </div>

          {/* Filters Section */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-900">Search & Filter</h2>
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4 text-slate-500" />
                <Select
                  value={filters.orderBy}
                  onValueChange={(v) => setFilters({ ...filters, orderBy: v as typeof filters.orderBy })}
                >
                  <SelectTrigger className="w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="priceAsc">Price: Low to High</SelectItem>
                    <SelectItem value="priceDesc">Price: High to Low</SelectItem>
                    <SelectItem value="mostViewed">Most Viewed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Listing Type</label>
                <Select value={filters.listingType || "all"} onValueChange={(v) => setFilters({ ...filters, listingType: v === "all" ? undefined : (v as "rent" | "sale") })}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="rent">For Rent</SelectItem>
                    <SelectItem value="sale">For Sale</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">BHK</label>
                <Select value={filters.bhk?.toString() || "all"} onValueChange={(v) => setFilters({ ...filters, bhk: v === "all" ? undefined : parseInt(v) })}>
                  <SelectTrigger>
                    <SelectValue placeholder="All BHK" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All BHK</SelectItem>
                    <SelectItem value="1">1 BHK</SelectItem>
                    <SelectItem value="2">2 BHK</SelectItem>
                    <SelectItem value="3">3 BHK</SelectItem>
                    <SelectItem value="4">4 BHK</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Apartment Type</label>
                <Select value={filters.apartmentType || "all"} onValueChange={(v) => setFilters({ ...filters, apartmentType: v === "all" ? undefined : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="flat">Flat</SelectItem>
                    <SelectItem value="villa">Villa</SelectItem>
                    <SelectItem value="studio">Studio</SelectItem>
                    <SelectItem value="penthouse">Penthouse</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Facing</label>
                <Select value={filters.facing || "all"} onValueChange={(v) => setFilters({ ...filters, facing: v === "all" ? undefined : v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Facings" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Facings</SelectItem>
                    <SelectItem value="North">North</SelectItem>
                    <SelectItem value="South">South</SelectItem>
                    <SelectItem value="East">East</SelectItem>
                    <SelectItem value="West">West</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Min Price (₹)</label>
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters.minPrice || ""}
                  onChange={(e) => setFilters({ ...filters, minPrice: e.target.value ? parseInt(e.target.value) : undefined })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Max Price (₹)</label>
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters.maxPrice || ""}
                  onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value ? parseInt(e.target.value) : undefined })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Min Area (sq ft)</label>
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters.minArea || ""}
                  onChange={(e) => setFilters({ ...filters, minArea: e.target.value ? parseInt(e.target.value) : undefined })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Max Area (sq ft)</label>
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters.maxArea || ""}
                  onChange={(e) => setFilters({ ...filters, maxArea: e.target.value ? parseInt(e.target.value) : undefined })}
                />
              </div>
            </div>

            <Button onClick={clearFilters} variant="outline" className="w-full">
              Clear Filters
            </Button>
          </div>

          {/* Results count */}
          {listings && (
            <p className="text-sm text-slate-500 mb-4">
              {listings.total} listing{listings.total !== 1 ? "s" : ""} found
            </p>
          )}

          {/* Listings Grid */}
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="animate-spin w-8 h-8 text-blue-600" />
            </div>
          ) : listings?.listings && listings.listings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.listings.map((listing) => (
                <Card
                  key={listing.id}
                  className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                  onClick={() => navigate(`/apartments/${listing.id}`)}
                >
                  {listing.photos && listing.photos.length > 0 ? (
                    <div className="w-full h-48 bg-slate-200 overflow-hidden">
                      <img src={listing.photos[0]} alt="Apartment" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                  ) : (
                    <div className="w-full h-48 bg-slate-100 flex items-center justify-center">
                      <Home className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">Flat {listing.flatNumber}</h3>
                        <p className="text-sm text-slate-500 capitalize">
                          {listing.apartmentType} · {formatDistanceToNow(new Date(listing.createdAt), { addSuffix: true })}
                        </p>
                      </div>
                      <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium capitalize shrink-0">
                        {listing.listingType}
                      </span>
                    </div>

                    <div className="space-y-1.5 mb-3">
                      <div className="flex items-center text-slate-700 text-sm">
                        <Bed className="w-4 h-4 mr-2 text-slate-400" />
                        {listing.bhk} BHK
                      </div>
                      <div className="flex items-center text-slate-700 text-sm">
                        <Maximize2 className="w-4 h-4 mr-2 text-slate-400" />
                        {listing.areaInSqFt} sq ft
                      </div>
                      {listing.facing && (
                        <div className="flex items-center text-slate-700 text-sm">
                          <Compass className="w-4 h-4 mr-2 text-slate-400" />
                          {listing.facing} Facing
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-xl font-bold text-slate-900">₹{listing.price.toLocaleString("en-IN")}</span>
                        {listing.listingType === "rent" && <span className="text-sm text-slate-500">/month</span>}
                      </div>
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={(e) => { e.stopPropagation(); navigate(`/apartments/${listing.id}`); }}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-lg text-slate-600">No listings found matching your criteria</p>
              <Button onClick={clearFilters} variant="outline" className="mt-4">Clear Filters</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
